import type { ButtonHTMLAttributes, ComponentProps, FC, ReactNode } from 'react'

import Link from 'next/link'
import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'ghost'
    | 'navy'
    | 'white'
    | 'danger'
    | 'danger-ghost'
    | 'link'

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'

export type ButtonVisualState = 'hover' | 'active' | 'focus'

const BASE = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-[color,background-color,border-color,box-shadow,transform] duration-150 select-none cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 active:scale-[0.98] [&_svg]:shrink-0'

const VARIANTS: Record<ButtonVariant, string> = {
    'primary': 'bg-primary-strong text-white shadow-card hover:bg-primary-deep',
    'secondary': 'bg-primary-soft text-primary-deep hover:bg-primary/15',
    'outline': 'border border-input bg-card text-foreground hover:border-primary/40 hover:bg-accent hover:text-primary-deep',
    'ghost': 'text-muted-foreground hover:bg-secondary hover:text-foreground',
    'navy': 'bg-navy text-white hover:bg-navy/90',
    'white': 'bg-white text-navy shadow-card hover:bg-white/90',
    'danger': 'bg-destructive text-white hover:bg-destructive/90',
    'danger-ghost': 'text-destructive hover:bg-destructive-soft',
    'link': 'h-auto px-0 text-primary-strong underline-offset-4 hover:text-primary-deep hover:underline'
}

const FORCED_HOVER: Record<ButtonVariant, string> = {
    'primary': 'bg-primary-deep',
    'secondary': 'bg-primary/15',
    'outline': 'border-primary/40 bg-accent text-primary-deep',
    'ghost': 'bg-secondary text-foreground',
    'navy': 'bg-navy/90',
    'white': 'bg-white/90',
    'danger': 'bg-destructive/90',
    'danger-ghost': 'bg-destructive-soft',
    'link': 'text-primary-deep underline'
}

const SIZES: Record<ButtonSize, string> = {
    'sm': 'h-8 px-3 text-small [&_svg]:size-4',
    'md': 'h-10 px-4 text-sm [&_svg]:size-4',
    'lg': 'h-12 px-6 text-body font-semibold [&_svg]:size-5',
    'icon': 'size-10 [&_svg]:size-5',
    'icon-sm': 'size-8 [&_svg]:size-4'
}

type VariantOptions = {
    variant?: ButtonVariant
    size?: ButtonSize
    visualState?: ButtonVisualState
    className?: string
}

export const buttonVariants = ({
    variant = 'primary',
    size = 'md',
    visualState,
    className
}: VariantOptions = {}): string => {
    return cn(
        BASE,
        VARIANTS[variant],
        variant === 'link' ? 'h-auto px-0' : SIZES[size],
        visualState === 'hover' && FORCED_HOVER[variant],
        visualState === 'active' && cn(FORCED_HOVER[variant], 'scale-[0.98]'),
        visualState === 'focus' && 'ring-2 ring-primary/45 ring-offset-2 ring-offset-background',
        className
    )
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    visualState?: ButtonVisualState
    loading?: boolean
    children?: ReactNode
}

export const Button: FC<ButtonProps> = ({
    variant,
    size,
    visualState,
    loading = false,
    disabled,
    className,
    children,
    type = 'button',
    ...props
}): ReactNode => {
    return (
        <button
            type={type}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            className={buttonVariants({ variant, size, visualState, className })}
            {...props}
        >
            {loading && <LoaderCircle className='animate-spin' aria-hidden='true' />}
            {children}
        </button>
    )
}

interface ButtonLinkProps extends ComponentProps<typeof Link> {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
    children?: ReactNode
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    variant,
    size,
    className,
    children,
    ...props
}): ReactNode => {
    return (
        <Link className={buttonVariants({ variant, size, className })} {...props}>
            {children}
        </Link>
    )
}
